import React, { useState } from 'react';
import {
  Card,
  Button,
  CardSection,
  EmptyLayout,
  Gutter,
  LayoutItem,
  Layout,
  IconButton,
  SmallHeader,
  Paragraph,
  colors,
  Flashy,
  useBreakpoint,
  useApi,
} from '@axiom/ui';
import type { Candidate } from '@axiom/validation';

import { CandidateProfileApi } from '../../api/candidate-profile-api';
import TransparentVectorBkg from '../../public/svg/background-effect-1.svg?url';
import PhoneReactionsGraphic from '../../public/svg/phone-reactions.svg';
import { localStorageUtil } from '../../utils/local-storage-util';

export type TextMessageAlertsBannerProps = {
  name?: string;
  candidate: Candidate;
};

export const TextMessageAlertsBanner = ({
  name = 'TEXT_MESSAGE_ALERTS_BANNER',
  candidate,
}: TextMessageAlertsBannerProps) => {
  const [{ data: candidateProfile }] = useApi(
    CandidateProfileApi.readCandidateProfile(candidate.id)
  );

  const localStorage = localStorageUtil.textMessageAlertsBannerDismissed(
    candidate.id
  );

  const [isDismissed, setIsDismissed] = useState(localStorage.get());
  const { isMobile } = useBreakpoint();
  const {
    communicationAlertsConfigured,
    communicationAlertsTextMessagesEnabled,
  } = candidateProfile;

  const handleOnClick = () => {
    const timestamp = new Date().toISOString();
    localStorage.set(timestamp);
    setIsDismissed(timestamp);
  };

  const isEligibleForBanner =
    !communicationAlertsConfigured && communicationAlertsTextMessagesEnabled;
  const isVisible = isEligibleForBanner && !isDismissed && !isMobile;

  if (!isVisible) {
    return null;
  }

  return (
    <Gutter bottom="16px">
      <Card name={name}>
        <CardSection
          background={{
            image: TransparentVectorBkg,
            color: colors.controlHoverPrimary,
            position: 'right top',
            size: 'contain',
          }}
        >
          <EmptyLayout
            graphic={
              <Gutter left="auto" right="auto" gutterType="margin">
                <PhoneReactionsGraphic width="119px" height="120px" />
              </Gutter>
            }
            graphicPosition="left"
          >
            <Layout>
              <LayoutItem>
                <Flashy color="textReversed">
                  <SmallHeader name="BANNER_HEADER">
                    Text message alerts are here – set yours up now!
                  </SmallHeader>
                </Flashy>
                <Gutter bottom="8px" />
                <Flashy color="textReversed">
                  <Paragraph name="DESCRIPTION_TEXT">
                    Get personalized reminders for timesheets, interviews,
                    opportunities, and more. Set your alert preferences now to
                    start receiving the reminders you want.
                  </Paragraph>
                </Flashy>
                <Gutter bottom="24px" />
                <Button
                  name="PENDO_TEXT_MESSAGES_ALERTS_BANNER_NAVIGATE_BUTTON"
                  pattern="secondary"
                  variation="filled"
                  inverse
                  to="../preferences/communications"
                  onClick={handleOnClick}
                >
                  Set up alerts
                </Button>
              </LayoutItem>
              <LayoutItem>
                <IconButton
                  name="PENDO_TEXT_MESSAGES_ALERTS_BANNER_CLOSE_BANNER_BUTTON"
                  icon="x-mark"
                  variation="minimal"
                  pattern="secondary"
                  inverse
                  onClick={handleOnClick}
                />
              </LayoutItem>
            </Layout>
          </EmptyLayout>
        </CardSection>
      </Card>
    </Gutter>
  );
};
